import {
  Button,
  Divider,
  IconButton,
  Menu,
  StatusIndicator,
  Tag,
  TextInput,
  Typography,
  toast,
} from '@neo4j-ndl/react';
import {
  EllipsisHorizontalIconOutline,
  MagnifyingGlassIconOutline,
  ShieldExclamationIconOutline,
} from '@neo4j-ndl/react/icons';
import type { Token } from '@nx/state';
import { consoleApi, fleetManagementApi, useActiveProject, useActiveProjectSummary } from '@nx/state';
import { isNonEmptyString } from '@nx/stdlib';
import { BackButton, DataGridHelpers } from '@nx/ui';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { add, formatDistance } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiErrorBanner } from '../shared/components';
import { withUpxContext } from '../shared/components/upx-context';
import { TextCell } from '../shared/helpers/table-overrides';
import { timePeriodFormatter } from '../shared/ui-helpers';
import { useTokens } from './hooks/use-tokens';
import CreateTokenModal, { RegenerateTokenModal } from './modals/create-token-modal';
import withProjectCapabilityGuard from './shared/components/project-capability-guard';
import { UnknownUser } from './shared/components/unknown-user';

const columnHelper = createColumnHelper<Token>();

const TokenActions = ({
  token,
  openRenerateTokenModalWith,
}: {
  token: Token;
  openRenerateTokenModalWith: (b: Token) => void;
}) => {
  const activeProjectSummary = useActiveProjectSummary();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [releaseToken] = fleetManagementApi.useReleaseTokenMutation();
  const [revokeToken] = fleetManagementApi.useRevokeTokenMutation();
  const [deleteToken] = fleetManagementApi.useDeleteTokenMutation();

  return (
    <div>
      <IconButton
        className="flex w-8 flex-row-reverse"
        onClick={() => {
          setIsOpen(true);
        }}
        ref={anchorEl}
        ariaLabel={'Token actions'}
        isClean={true}
      >
        <EllipsisHorizontalIconOutline />
      </IconButton>
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} anchorRef={anchorEl}>
        <Menu.Items>
          {token.claimed_by !== 'REVOKED' && (
            <>
              <Menu.Item
                title="Regenerate"
                description="Recreate a token with same name and new expiry"
                onClick={() => openRenerateTokenModalWith(token)}
              />
              <Menu.Item
                title="Release"
                description="Remove the token claim to reuse the token"
                onClick={() => {
                  releaseToken({ projectId: activeProjectSummary?.id ?? '', tokenId: token.id })
                    .then((tokenRes) => {
                      if (!tokenRes.error) {
                        toast.success(`Token released`, { shouldAutoClose: true });
                      } else {
                        toast.danger(<ApiErrorBanner error={tokenRes.error} />, { shouldAutoClose: true });
                      }
                    })
                    .catch((err: Error) => {
                      toast.danger(err.message, { shouldAutoClose: true });
                    });
                }}
              />
              <Divider />
            </>
          )}
          <>
            {token.claimed_by !== 'REVOKED' && (
              <Menu.Item
                title="Revoke"
                className="text-warning-text"
                onClick={() => {
                  revokeToken({ projectId: activeProjectSummary?.id ?? '', tokenId: token.id })
                    .then((tokenRes) => {
                      if (!tokenRes.error) {
                        toast.success(`Token revoked`, { shouldAutoClose: true });
                      } else {
                        toast.danger(<ApiErrorBanner error={tokenRes.error} />, { shouldAutoClose: true });
                      }
                    })
                    .catch((err: Error) => {
                      toast.danger(err.message, { shouldAutoClose: true });
                    });
                }}
              />
            )}
            <Menu.Item
              title="Delete"
              className="text-danger-text"
              onClick={() => {
                deleteToken({ projectId: activeProjectSummary?.id ?? '', tokenId: token.id })
                  .then((tokenRes) => {
                    if (!tokenRes.error) {
                      toast.success(`Token deleted`, { shouldAutoClose: true });
                    } else {
                      toast.danger(<ApiErrorBanner error={tokenRes.error} />, { shouldAutoClose: true });
                    }
                  })
                  .catch((err: Error) => {
                    toast.danger(err.message, { shouldAutoClose: true });
                  });
              }}
            />
          </>
        </Menu.Items>
      </Menu>
    </div>
  );
};

const TokenManagement = () => {
  const { tokens, isLoading, error } = useTokens();
  const [isGenTokenModalOpen, setIsGenTokenModalOpen] = useState<boolean>(false);
  const [isRegenTokenModalOpen, setIsRegenTokenModalOpen] = useState<boolean>(false);
  const [tokenInContext, setTokenInContext] = useState<Token>();
  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: TextCell,
      }),
      columnHelper.display({
        header: 'Status',
        enableGlobalFilter: true,
        cell: ({ row }) => {
          if (!row.original.claimed_by) {
            return (
              <div className="flex items-center gap-1">
                <StatusIndicator type="unknown" />
                <Typography variant="body-medium">Not in use</Typography>
              </div>
            );
          } else if (row.original.claimed_by === 'REVOKED') {
            return (
              <div className="flex items-center gap-1">
                <StatusIndicator type="danger" />
                <Typography variant="body-medium">Revoked</Typography>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1">
              <StatusIndicator type="success" />
              <Typography variant="body-medium">In use</Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor('claimed_by', {
        header: 'Claimed by',
        cell: TextCell,
      }),
      columnHelper.accessor('expiry_time', {
        header: 'Expires on',
        cell: ({ row }) => timePeriodFormatter(new Date(row.original.expiry_time)),
      }),
      columnHelper.accessor('creation_time', {
        header: 'Creation time',
        cell: ({ row }) => timePeriodFormatter(new Date(row.original.creation_time)),
      }),
      columnHelper.accessor(
        (row) => {
          return getProjectMembersRes.data?.find((member) => member.userId === row.created_by)?.email;
        },
        {
          header: 'Created by',
          cell: (cx) => {
            const value = cx.getValue();
            if (isNonEmptyString(value)) {
              return <DataGridHelpers.TruncatedColumn value={value} />;
            }
            return <UnknownUser userId={cx.row.original.created_by} />;
          },
        },
      ),
      columnHelper.accessor('auto_rotated', {
        header: 'Auto-rotated',
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        cell: ({ row }) => <Typography variant="body-medium">{row.original.auto_rotated ? 'Yes' : 'No'}</Typography>,
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <TokenActions
            token={row.original}
            openRenerateTokenModalWith={(token) => {
              setTokenInContext(token);
              setIsRegenTokenModalOpen(true);
            }}
          />
        ),
      }),
    ],
    [getProjectMembersRes.data],
  );

  const table = useReactTable<Token>({
    columns: columns,
    data: tokens,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (error) {
    return <ApiErrorBanner className="m-4" error={error} />;
  }

  return (
    <div className="token-management mt-4 sm:mx-4 md:mx-8 xl:mx-auto xl:max-w-[1680px] xl:px-8">
      <div className="mt-4 flex flex-col gap-4">
        <BackButton to="../instances/?instancesTab=self-managed" />
        <Typography variant="h4">Token management</Typography>
      </div>
      <div className="mt-6">
        <DataGridHelpers.OuterHeader className="bg-palette-neutral-bg-weak flex justify-between rounded-t-2xl">
          <div className="flex basis-[400px]">
            <TextInput
              isFluid={true}
              size="medium"
              placeholder="Search"
              leftElement={<MagnifyingGlassIconOutline />}
              onChange={(e) => table.setGlobalFilter(String(e.target.value))}
              htmlAttributes={{ 'aria-label': 'Search metrics integration status table' }}
            />
          </div>
          <Button onClick={() => setIsGenTokenModalOpen(!isGenTokenModalOpen)}>Create token</Button>
        </DataGridHelpers.OuterHeader>
        <DataGridHelpers.Wrapper className="rounded-t-none">
          <DataGridHelpers.DataGridRightColumnPinned
            tableInstance={table}
            isResizable={false}
            styling={{ headerStyle: 'clean', borderStyle: 'horizontal' }}
            rootProps={{
              'aria-label': 'Status table',
            }}
            isLoading={isLoading}
            components={{ RowsPerPage: DataGridHelpers.RowsPerPageWithBodyMenuPortal }}
          />
        </DataGridHelpers.Wrapper>
        {isGenTokenModalOpen && (
          <CreateTokenModal isOpen={isGenTokenModalOpen} closeModal={() => setIsGenTokenModalOpen(false)} />
        )}
        {tokenInContext && (
          <RegenerateTokenModal
            isOpen={isRegenTokenModalOpen}
            closeModal={() => setIsRegenTokenModalOpen(false)}
            tokenInContext={tokenInContext}
          />
        )}
      </div>
    </div>
  );
};

export const TokenStatus = ({ token }: { token: Token | undefined }) => {
  const expiryTime = new Date(token?.expiry_time ?? 0).getTime();
  const activeProject = useActiveProject();
  const navigate = useNavigate();

  // start notifying 7 days before expiry
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (
    token !== undefined &&
    (!token.auto_rotated || expiryTime < new Date().getTime()) &&
    expiryTime < add(new Date(), { days: 7 }).getTime()
  ) {
    return (
      <div
        onClick={() => {
          void navigate(`/projects/${activeProject.id}/instances/token-management`);
        }}
        className="cursor-pointer"
      >
        <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
          <ShieldExclamationIconOutline className="h-4 w-4" />
          <Typography variant="body-medium">
            {expiryTime < new Date().getTime() ? 'Token expired' : 'Token expires'}{' '}
            {formatDistance(token.expiry_time, new Date(), { addSuffix: true })}
          </Typography>
        </Tag>
      </div>
    );
  }

  return null;
};

const TokenManagementWithUpxContext = withUpxContext(withProjectCapabilityGuard(TokenManagement));

export default TokenManagementWithUpxContext;
